/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { Menu, Spacer, colors, sizes } from '@smartsheet/lodestar-core';
import {
  CloseIcon,
  KebabIcon,
  PaginationArrowLeftIcon,
} from '@smartsheet/lodestar-icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  setActiveTab,
  setshowRunStepsPanel,
} from '../../../../../../services/ducks/designerDuck/designerDuck';
import { selectedRunSelector } from '../../../../../../services/ducks/workflowRunDuck/workflowRunDuck';
import {
  PANEL_HEADER_HEIGHT,
  PANEL_HEADER_SPACE_BOTTOM,
} from '../../../../../../utils/dimensions';
import { Column } from '../../../../../common/Column';
import { IconButtonNoSpacing } from '../../../../../common/IconButton';
import { runLogMenuOptions } from '../../RunLogMenuOptions';

export const PanelButton = styled.div`
  height: 28px;
  width: 28px;
  border-radius: ${sizes.xxSmall}px;
  cursor: pointer;
  background: ${(props: { active?: boolean }) =>
    props.active ? colors.neutralLight30 : 'none'};
  &:hover {
    background: ${colors.neutralLight30};
  }
  span {
    margin-top: 2px;
    margin-left: 2px;
  }
`;

const panelHeaderStyle = css`
  justify-content: space-between;
  padding-bottom: ${PANEL_HEADER_SPACE_BOTTOM}px;
  align-items: center;
  border-bottom: 2px solid #e0e0e0;
  height: ${PANEL_HEADER_HEIGHT}px;
`;

const VerticalBar = styled.div`
  width: 2px;
  background: ${colors.neutralLight10};
  height: 24px;
`;

const leftColumnStyle = css`
  column-gap: ${sizes.xSmall}px;
  flex-grow: 1;
`;

const rightColumnStyle = css`
  justify-content: flex-end;
  flex-grow: 1;
`;

const middleColumnStyle = css`
  flex-grow: 2;
  justify-content: center;
  span {
    font-weight: 600;
    color: ${colors.neutralDark20};
  }
`;

const backButtonStyle = css`
  &&& {
    margin-left: -8px;
  }
  span {
    line-height: 24px;
  }
  span:nth-child(2) {
    margin-left: 8px;
  }
`;

export const PanelHeader = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const selectedRun = useSelector(selectedRunSelector);
  const ref = useRef(null);

  if (!selectedRun) {
    return null;
  }

  const onClose = () => {
    batch(() => {
      dispatch(setActiveTab(null));
      dispatch(setshowRunStepsPanel(false));
    });
  };

  const backHandler = () => {
    dispatch(setshowRunStepsPanel(false));
  };

  const createTime = new Date(selectedRun.createTime / 1000000);
  return (
    <Spacer orientation="row" space="small" css={panelHeaderStyle}>
      <Column css={leftColumnStyle}>
        <IconButtonNoSpacing
          onClick={backHandler}
          appearance="borderless"
          css={backButtonStyle}
          aria-label={translate('designer:runLog.viewAllRuns')}
        >
          <PaginationArrowLeftIcon />
          <span>{translate('designer:runLog.viewAllRuns')}</span>
        </IconButtonNoSpacing>
      </Column>
      <Column css={middleColumnStyle}>
        <span>
          {createTime.toLocaleDateString()}{' '}
          {createTime.toLocaleTimeString('en-US', {
            hour12: true,
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </Column>
      <Column css={rightColumnStyle}>
        <PanelButton
          ref={ref}
          active={isOptionsMenuOpen}
          onClick={() => setIsOptionsMenuOpen(true)}
        >
          <KebabIcon />
        </PanelButton>
        <Menu
          aria-label={translate('common:options')}
          placement="bottom-end"
          targetElement={ref}
          isOpen={isOptionsMenuOpen}
          onCloseRequested={() => setIsOptionsMenuOpen(false)}
          id="xx"
        >
          {runLogMenuOptions}
        </Menu>
        <VerticalBar />
        <PanelButton onClick={onClose}>
          <CloseIcon />
        </PanelButton>
      </Column>
    </Spacer>
  );
};
